import * as Yup from 'yup'
import {
  apenasDigitos,
  CargoType,
  createValidation,
} from '../../../../../../../../_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { identificadoresShape } from 'app/modules/apps/shared/documentos-pessoa/identificadores'

export const funcionarioSchema = (
  t: TFunction,
  showEndereco: boolean,
  emEdicao: boolean,
  documentoEstrangeiroNaLista: boolean,
  cpfOriginal?: string,
  cnsOriginal?: string
) => {
  const v = createValidation(t)

  return Yup.object().shape({
    empresa: Yup.object().nullable().required(v.requiredField('empresa', 'select')),
    funcionario: Yup.object().shape({
      id: Yup.number().notRequired(),
      primeiroNome: Yup.string()
        .min(3, 'Minimo de 3 caracteres')
        .max(50, 'Máximo de 50 caracteres')
        .required('O campo nome é obrigatório')
        .test(
          'not-empty',
          'Campo não pode conter apenas espaços',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      sobreNome: Yup.string()
        .min(3, 'Minimo de 3 caracteres')
        .max(50, 'Máximo de 50 caracteres')
        .required('O campo sobrenome é obrigatório')
        .test(
          'not-empty',
          'Campo não pode conter apenas espaços',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      ...identificadoresShape(t, documentoEstrangeiroNaLista),
      // Compara só os dígitos: o input mascarado reformata o valor vindo da API
      // no mount (12345678901 -> 123.456.789-01) e a comparação literal acusaria
      // uma alteração que o usuário não fez.
      justificativa: Yup.string().when(['cpf', 'cns'], {
        is: (cpf?: string, cns?: string) =>
          emEdicao &&
          (apenasDigitos(cpf) !== apenasDigitos(cpfOriginal) ||
            apenasDigitos(cns) !== apenasDigitos(cnsOriginal)),
        then: (schema) =>
          schema.required('A justificativa é obrigatória quando o CPF/CNS é alterado'),
      }),

      // As regras de tamanho ficam dentro do `when`: o campo só é exibido para
      // cargo Médico, então aplicá-las fora bloqueia o submit de qualquer outro
      // cargo (crm vazio) sem exibir erro em tela.
      crm: Yup.string().when('cargo.value', {
        is: (cargo: CargoType) => cargo === CargoType.Medico,
        then: (schema) =>
          schema
            .trim()
            .min(1, 'Mínimo de 1 caractere')
            .max(10, 'Máximo de 10 caracteres')
            .required('O campo registro no conselho é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      rqe: Yup.string().notRequired(),
      email: Yup.string().email('Formato inválido').required('E-mail é obrigatório').nullable(),
      // Aceita vazio (campo opcional) e telefone fixo, que tem 10 dígitos contra
      // os 11 do celular.
      telefone: Yup.string()
        .notRequired()
        .nullable()
        .test('telefone-valido', 'Número Inválido', (value) => {
          if (!value) return true
          const digitos = value.replace(/\D/g, '').length
          return digitos === 10 || digitos === 11
        }),
      sexo: Yup.object().nullable().required('Selecione um gênero'),
      nascimento: Yup.string().required('O campo data de nascimento é obrigatório'),
      cargo: Yup.object().nullable().required('Selecione um cargo'),
      especialidades: Yup.array().of(Yup.object()).nullable().notRequired(),
      unidades: Yup.array().of(Yup.object().notRequired()).nullable().notRequired(),
      uf: Yup.object()
        .nullable()
        .when('cargo.value', {
          is: (cargo: CargoType) => cargo === CargoType.Medico,
          then: (schema) => schema.required('O campo UF é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
      vinculo: Yup.object().nullable().notRequired(),
      conselho: Yup.object()
        .nullable()
        .when('cargo.value', {
          is: (cargo: CargoType) => cargo === CargoType.Medico,
          then: (schema) => schema.required('Conselho é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
      raca: Yup.object().nullable().notRequired(),
      genero: Yup.object().nullable().notRequired(),
      nacionalidade: Yup.object().nullable().notRequired(),
      faturamentoMinimo: Yup.number()
        .nullable()
        .notRequired()
        .integer('O quantidade mínima deve ser um número inteiro')
        .min(1, 'O valor precisa ser maior de 1'),
      valorAtendimento: Yup.number()
        .nullable()
        .notRequired()
        .min(1, 'O valor precisa ser maior que R$1'),
      valorRetorno: Yup.number()
        .nullable()
        .notRequired()
        .min(1, 'O valor precisa ser maior que R$1'),
    }),

    endereco: Yup.object().shape({
      // O `matches` precisa ficar dentro do `when`: fora dele a string vazia de
      // um funcionário sem endereço reprova o formulário inteiro, e o campo nem
      // está renderizado para mostrar o erro.
      cep: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .required('O campo CEP é obrigatório')
            .matches(/^\d{2}\.\d{3}-\d{3}$/, 'Formato deve ser 00.000-000'),
        otherwise: (schema) => schema.notRequired(),
      }),
      logradouro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(5, 'Minimo de 5 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo logradouro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      numero: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) => schema.required('O campo número é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      complemento: Yup.string().notRequired(),
      bairro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo bairro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      cidade: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo cidade é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      uf: Yup.object()
        .nullable()
        .when([], {
          is: () => showEndereco,
          then: (schema) => schema.required('O campo UF é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
  })
}
